import Chip from '@mui/material/Chip';
import { ThemeMode } from '@app/types/config';
import Avatar from '@mui/material/Avatar';
import { IconSettings } from '@tabler/icons-react';
import useConfig from '@hooks/useConfig';
import { useTheme } from '@mui/material/styles';
import { MutableRefObject } from 'react';
import { useAuthSelector } from '@providers/AuthProvider/ui/AuthProvider.tsx';
import { getUserInitials } from '@utils/utils';

type HeaderUserAvatarProps = {
  open: boolean;
  handleToggle: () => void;
  anchorRef: MutableRefObject<any>;
};

function HeaderUserAvatar({
  open,
  handleToggle,
  anchorRef,
}: HeaderUserAvatarProps) {
  const theme = useTheme();
  const { mode } = useConfig();
  const userData = useAuthSelector((state) => state.userData);
  const renewUserImageLink = useAuthSelector(
    (state) => state.renewUserImageLink
  );

  const userInitials = userData?.name ? getUserInitials(userData.name) : '';
  const hasPhoto = Boolean(userData?.image_url);

  return (
    <Chip
      sx={{
        ml: 2,
        height: '48px',
        alignItems: 'center',
        borderRadius: '27px',
        transition: 'all .2s ease-in-out',
        borderColor: mode === ThemeMode.DARK ? 'dark.main' : 'primary.light',
        bgcolor: mode === ThemeMode.DARK ? 'dark.main' : 'primary.light',
        '&[aria-controls="menu-list-grow"], &:hover': {
          borderColor: 'primary.main',
          bgcolor: `${theme.palette.primary.main} !important`,
          color: 'primary.light',
          '& svg': {
            stroke: theme.palette.primary.light,
          },
        },
        '& .MuiChip-label': {
          lineHeight: 0,
        },
      }}
      icon={
        <Avatar
          src={hasPhoto ? userData?.image_url : undefined}
          alt="user-images"
          imgProps={{ onError: renewUserImageLink }}
          sx={{
            ...theme.typography.mediumAvatar,
            margin: '8px 0 8px 8px !important',
            cursor: 'pointer',
            backgroundColor: hasPhoto ? undefined : theme.palette.primary.main,
            color: hasPhoto ? undefined : 'white',
            fontSize: hasPhoto ? undefined : '12px',
          }}
          ref={anchorRef}
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          color="inherit"
        >
          {!hasPhoto && userInitials}
        </Avatar>
      }
      label={<IconSettings stroke={1.5} size="24px" />}
      variant="outlined"
      ref={anchorRef}
      aria-controls={open ? 'menu-list-grow' : undefined}
      aria-haspopup="true"
      onClick={handleToggle}
      color="primary"
      aria-label="user-account"
    />
  );
}

export default HeaderUserAvatar;
